/* eslint-disable arrow-body-style */
import { PButton, PButtonGroup, PModal } from '@porsche-design-system/components-react';
import { spacingFluidSmall } from '@porsche-design-system/components-react/styles';
import { type ButtonProps, type ModalProps } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useThemeComponents } from '..';

const HeaderContainer = styled.div`
    font-size: 24px;
    font-weight: 900;
`;

type DefaultFooterProps = Pick<ModalProps, 'okText' | 'cancelText' | 'onOk' | 'onCancel'> & {
    okButtonProps?: Pick<ButtonProps, 'htmlType' | 'form'>;
    cancelButtonProps?: Pick<ButtonProps, 'disabled' | 'style'>;
};

const DefaultFooter = ({
    okText = 'OK',
    cancelText = 'Cancel',
    onOk = () => {},
    onCancel = () => {},
    okButtonProps,
    cancelButtonProps,
}: DefaultFooterProps) => {
    const { Button } = useThemeComponents();

    return (
        <PButtonGroup className="footer">
            <Button className="ok-button" data-cy="ok-button" onClick={onOk} type="primary" block {...okButtonProps}>
                {okText}
            </Button>
            <PButton
                className="cancel-button"
                data-cy="cancel-button"
                icon="close"
                onClick={onCancel}
                type="button"
                variant="secondary"
                {...cancelButtonProps}
            >
                {cancelText}
            </PButton>
        </PButtonGroup>
    );
};

const Modal = ({
    children,
    title,
    open,
    visible,
    okText,
    cancelText,
    onOk,
    onCancel,
    okButtonProps,
    cancelButtonProps,
    footer: inputFooter,
    maskClosable = true,
    closable = false,
    width,
    ...props
}: ModalProps) => {
    const widget = useRef<HTMLElement>();

    // Destructure DefaultFooterProps properties
    const footerProps: DefaultFooterProps = { cancelText, okText, onCancel, onOk, okButtonProps, cancelButtonProps };

    // set footer to null if you don't need default footer buttons.
    // DefaultFooter will be shown only if input footer is undefined.
    const footer = useMemo(
        () => (inputFooter === undefined ? <DefaultFooter {...footerProps} /> : inputFooter),
        [inputFooter, footerProps]
    );

    const [onReady, setOnReady] = useState<boolean>(false);

    // observes changes in the Shadow DOM of the modal widget to detect when it's ready.
    // if the modal's Shadow DOM has a first child, it indicates readiness.
    useEffect(() => {
        if (widget.current?.shadowRoot) {
            const { shadowRoot } = widget.current;

            const rootNode = shadowRoot?.getRootNode();
            if (rootNode) {
                const observer = new MutationObserver(() => {
                    setOnReady(value => value || !!rootNode?.firstChild);
                });
                // Start observing changes in the Shadow DOM
                observer.observe(rootNode, { attributes: true, childList: true, subtree: true });

                // disconnect the observer when the component unmounts
                return () => {
                    observer.disconnect();
                };
            }
        }

        return () => {};
    }, [widget.current?.shadowRoot]);

    // show/hide dismiss button
    // It is possible to not render the dismiss button by setting the `dismissButton={false}` attribute.
    // However, At the same time this also deactivates dismissing the modal by pressing `Escape`.
    useEffect(() => {
        const { shadowRoot } = widget.current;
        const rootNode = shadowRoot?.getRootNode() as Element;

        if (rootNode?.childNodes?.length > 0) {
            const controls = rootNode.querySelector('.controls');

            if (controls) {
                (controls as HTMLDivElement).style.display = closable ? 'flex' : 'none';
            }
        }
    }, [onReady, closable]);

    return (
        // @ts-ignore
        <PModal
            ref={widget}
            disableBackdropClick={!maskClosable}
            // ignore type for v3 design
            // @ts-ignore
            onDismiss={onCancel}
            open={open || visible}
            {...props}
        >
            {title && <HeaderContainer slot="heading">{title}</HeaderContainer>}
            <div
                style={{
                    maxWidth: '100%',
                    width: typeof width === 'number' ? `${width}px` : width || 'auto',
                    marginBottom: footer ? spacingFluidSmall : 0,
                }}
            >
                {children}
            </div>
            {footer && <PButtonGroup className="footer">{footer}</PButtonGroup>}
        </PModal>
    );
};

export default Modal;
